import {ChangeDetectorRef, Component, Input, OnDestroy, OnInit} from "@angular/core";
import {ResponsiveService} from "../../orchestration/responsive.service";
import {Subscription} from "rxjs";
import {ButtonProminence, ButtonTier} from "../../buttons/button/button";
import {MessageLevel} from "../../notification/message/message";
import {NavigationService} from "../../orchestration/navigation.service";
import {OrderModel} from "../../order/order.model";
import {OrderingContextModel} from "../ordering-context.model";
import {OrderingContextEvents} from "../ordering-context-events";
import {OrderEvents} from "../../order/order-events";
import {SessionEvents} from "../../orchestration/session-events";
import {OrderType} from "../../order/order";

@Component({
    selector: "woa-ordering-context-panel",
    templateUrl: "./ordering-context-panel.component.html"
})
export class OrderingContextPanelComponent implements OnInit, OnDestroy {
    @Input() timeOnly: boolean = false;

    OrderType = OrderType;
    ButtonTier = ButtonTier;
    ButtonProminence = ButtonProminence;
    MessageLevel = MessageLevel;
    OrderingContextEvents = OrderingContextEvents;

    subscriptions: Subscription = new Subscription();

    constructor(public orderingContextModel: OrderingContextModel,
                public orderModel: OrderModel,
                public responsiveService: ResponsiveService,
                private navigationService: NavigationService,
                private changeDetectorRef: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.subscriptions.add(SessionEvents.currentApp$.subscribe(() => this.changeDetectorRef.detectChanges()));
        this.subscriptions.add(OrderingContextEvents.storeDetailsChanged$.subscribe(() => this.changeDetectorRef.detectChanges()));
        this.subscriptions.add(OrderingContextEvents.deliveryDetailsChanged$.subscribe(() => this.changeDetectorRef.detectChanges()));
        this.subscriptions.add(OrderEvents.orderInitiationRequestReceived$.subscribe(() => this.changeDetectorRef.detectChanges()));
        this.subscriptions.add(OrderEvents.orderInitiationStatus$.subscribe(() => this.changeDetectorRef.detectChanges()));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    get summaryOrderType(): string {
        switch (this.orderingContextModel.orderType) {
            case OrderType.ORDER_AHEAD:
                return this.orderingContextModel.currentStore ? "woa.common.order.ahead.placement.verb" : undefined;
            case OrderType.ORDER_DELIVERY:
                return this.orderingContextModel.addressTrusted ? "woa.common.order.delivery.placement.verb" : undefined;
            case OrderType.ORDER_AT_TABLE:
                return this.summaryTable ? "woa.common.order.atTable.placement.verb" : undefined;
            default:
                return undefined;
        }
    }

    get showAsap(): boolean {
        return (this.orderingContextModel.orderType === OrderType.ORDER_AHEAD || this.orderingContextModel.orderType === OrderType.ORDER_DELIVERY) &&
            this.summaryOrderType && !this.orderingContextModel.expectedCompletion && this.orderingContextModel.canShowAsapSelectionTime;
    }

    get summaryTime(): string {
        return this.orderingContextModel.expectedCompletion &&
        (this.orderingContextModel.orderType === OrderType.ORDER_AHEAD ||
            (this.orderingContextModel.orderType === OrderType.ORDER_DELIVERY && !this.primaryAddress)) ? this.orderingContextModel.getFormattedExpectedCompletion()
            : undefined;
    }

    get summaryTable(): string {
        return this.orderingContextModel.currentTableNumberLabel;
    }

    private get showCurrentStore(): boolean {
        return this.orderingContextModel.currentStore &&
            ((this.orderingContextModel.orderType === OrderType.ORDER_AHEAD && !this.orderingContextModel.singleStoreLocation) ||
                (this.orderingContextModel.orderType === OrderType.ORDER_AT_TABLE && this.orderingContextModel.canChangeTableStore));
    }

    private get showAddress(): boolean {
        return this.orderingContextModel.orderType === OrderType.ORDER_DELIVERY && this.orderingContextModel.addressTrusted;
    }

    get primaryStore(): string {
        return this.showCurrentStore && this.query ? this.orderingContextModel.currentStore.collectiveStoreName : undefined;
    }

    get primaryAddress(): string {
        return this.showAddress && this.query ? this.orderingContextModel.address.street || this.orderingContextModel.address.city : undefined;
    }

    get query(): string {
        switch (this.orderingContextModel.orderType) {
            case OrderType.ORDER_AHEAD:
                return !this.orderingContextModel.currentStore && !this.timeOnly ? "woa.heading.ordering-context.selectStore.query"
                    : !this.orderingContextModel.expectedCompletion && this.orderingContextModel.canSelectOrderTime && !this.orderingContextModel.canShowAsapSelectionTime ? "woa.heading.ordering-context.selectTime.query"
                        : undefined;
            case OrderType.ORDER_DELIVERY:
                return !this.orderingContextModel.addressTrusted && !this.timeOnly ? "woa.heading.ordering-context.enterAddress.query"
                    : !this.orderingContextModel.currentStore && !this.timeOnly ? "woa.heading.ordering-context.selectStore.query"
                        : !this.orderingContextModel.expectedCompletion && this.orderingContextModel.canSelectOrderTime && !this.orderingContextModel.canShowAsapSelectionTime ? "woa.heading.ordering-context.selectTime.query"
                            : undefined;
            case OrderType.ORDER_AT_TABLE:
                return !this.summaryTable && (this.orderingContextModel.currentStore || !this.orderingContextModel.hasStoreGroup) ? "woa.heading.ordering-context.selectTable.query"
                    : !this.orderingContextModel.currentStore ? "woa.heading.ordering-context.selectStore.query"
                        : undefined;
            default:
                return "woa.heading.ordering-context.orderType.query";
        }
    }

    get secondaryStore(): string {
        return this.showCurrentStore && !this.query ? this.orderingContextModel.currentStore.collectiveStoreName : undefined;
    }

    get storeDetailsLink(): string {
        return this.orderingContextModel.orderType === OrderType.ORDER_AHEAD && this.orderingContextModel.currentStore && !this.query ? "woa.button.ordering-context.viewCurrentStore" : undefined;
    }

    get secondaryAddress(): string {
        return this.showAddress && !this.query ? this.orderingContextModel.address.street || this.orderingContextModel.address.city : undefined;
    }

    get showContextAction(): boolean {
        return this.orderingContextModel.canSelectOrderTime ||
            (!this.timeOnly &&
                (this.orderingContextModel.canChangeStore ||
                    !this.orderingContextModel.currentStore ||
                    !this.orderingContextModel.singleOrderType ||
                    this.orderingContextModel.orderType !== OrderType.ORDER_AHEAD));
    }

    get actionLabel(): string {
        return this.orderingContextModel.orderType == OrderType.ORDER_DELIVERY && !this.orderingContextModel.addressTrusted ? "woa.button.ordering-context.enter"
            : this.query ? "woa.button.ordering-context.select"
                : "woa.button.ordering-context.change";
    }

    get showSelectTimeLink(): boolean {
        return this.orderingContextModel.canSelectOrderTime && this.orderModel.initiatedOrder.unavailabilityReason !== "offline";
    }
}



// WEBPACK FOOTER //
// ./src/app/ordering-context/ordering-context-panel/ordering-context-panel.component.ts